/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHotkeys } from 'react-hotkeys-hook';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  category: 'ALL',
  subcat: 'ALL',
  supplier: 'ALL',
  order_by: 'pd.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function PosCheckPro({ setBlockScan, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notify, setNotify] = useState(null);
  const txtSearch = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const closeModal = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('OPTIONS', `/${mod}/checkpro`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      supplier: data.supplier,
      subcat: data.subcat,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setRecord(result.data);

          setPageTotal(result.total_pages);
          setPageNo(result.page);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const open = () => {
    setModalOpen(true);
    setBlockScan(true);
    view({ ...find, search: '', page: 1 });

    setTimeout(function () {
      txtSearch.current.focus();
    }, 500);
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const promotionDisplay = (inp) => {
    if (inp === 'A') return <code className='color-success'>A : ซื้อครบจำนวนลดราคา</code>;
    if (inp === 'B') return <code className='color-primary'>B : ซื้อครบมูลค่าได้ของแถม</code>;
    if (inp === 'C') return <code className='color-info'>C : ซื้อครบจำนวนได้ของแถม</code>;
    if (inp === 'D') return <code className='color-danger'>D : ยิ่งซื้อยิ่งลด</code>;
    if (inp === 'E') return <code className='color-warning'>E : ซื้อครบมูลค่าได้ส่วนลด</code>;
    return '';
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2}>รูป</TableCell>
                    <TableCell rowSpan={2}>Barcode</TableCell>
                    <TableCell rowSpan={2}>สินค้า</TableCell>
                    <TableCell align='center' colSpan={6}>
                      Campaign
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='left' width={300}>
                      Promotion
                    </TableCell>
                    <TableCell align='center'>เริ่ม</TableCell>
                    <TableCell align='center'>ถึง</TableCell>
                    <TableCell>ประเภท</TableCell>
                    <TableCell align='right' className='nowrap'>
                      ปริมาณ
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      ราคา
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.product_code}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox width={250}>
                          {row.campaign_name === '' ? 'ไม่มีโปรโมชั่น' : row.campaign_name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_start)}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_stop)}</TableCell>
                      <TableCell className='nowrap'>
                        {promotionDisplay(row.promotion_type)}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-info'>
                          {row.promotion_type === 'B'
                            ? ''
                            : Func.numberDisplay(row.campaign_quantity)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-success'>{Func.numberDisplay(row.price)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-open'
        fullWidth
        onClose={closeModal}
      >
        <DialogTitle>
          <i className='fad fa-gift-card color-danger' /> &nbsp; เช็คโปรโมชั่น
          <IconButton
            aria-label='close'
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  useHotkeys('a', () => open());

  return (
    <MDBox>
      <Tooltip title='KEY : A'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={open}
          disabled={loading || buttonLoading}
          ref={btnRef}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-gift-card' />
          )}
          &nbsp; เช็คโปร
        </MDButton>
      </Tooltip>

      {renderOpen()}
      {notify}
    </MDBox>
  );
}

PosCheckPro.propTypes = {
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosCheckPro;
